import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import '../styles/LeadsMap.css';
import { GoogleMap } from '@react-google-maps/api';
import { Marker, MapModeToggle } from './';
import LeadMapBubble from './LeadMapBubble';
import { rasterOnSafari } from '../utils/mapRendering';

function LeadsMap({
    leads,
    isMapLoaded,
    hoverLeadId,
    clickedLeadId,
    onLeadClick,
    onLeadHover,
    rawMap,
    setRawMap,
    mapType,
    setMapType,
    innerWidth,
    onViewportChange,
    viewportFilterActive,
    initialBounds,
    // Optional Set of leadIds that have just arrived live (Spencer's Deals
    // feed). When present, the matching marker plays a one-shot drop-in +
    // ripple. Other callers omit it and nothing changes.
    newLeadIds,
    // Optional Google Maps gestureHandling mode. Defaults to 'auto', which on
    // mobile behaves like 'cooperative' and shows the "use two fingers to move
    // the map" overlay. The map-first Spencer's Deals feed passes 'greedy' so a
    // single finger pans the map (no overlay).
    gestureHandling = 'auto',
    // Optional (lead) => 'PASS' | 'FAIL' | null. When provided, each marker
    // colours by the returned verdict (green/red) instead of showing a price
    // chip — the Co-Living Deals feed uses this so the map communicates parking
    // eligibility at a glance. Other callers omit it and the price chip renders.
    getMarkerVerdict = null
}) {
    // Calculate map center from all leads or default to center of US
    // When viewport filtering is active, freeze center to prevent map jumping on empty results
    const lastCenterRef = useRef({ lat: 39.8283, lng: -98.5795 });
    const mapCenter = useMemo(() => {
        if (viewportFilterActive) {
            return lastCenterRef.current;
        }
        let center;
        if (leads.length === 0) {
            center = { lat: 39.8283, lng: -98.5795 };
        } else {
            const sumLat = leads.reduce((sum, lead) => sum + lead.latitude, 0);
            const sumLng = leads.reduce((sum, lead) => sum + lead.longitude, 0);
            center = { lat: sumLat / leads.length, lng: sumLng / leads.length };
        }
        lastCenterRef.current = center;
        return center;
    }, [leads, viewportFilterActive]);

    const options = useMemo(() => ({
        disableDefaultUI: false,
        scrollwheel: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        keyboardShortcuts: false,
        gestureHandling,
        mapId: JSON.parse(process.env.REACT_APP_COMPS_MAP_ID).key,
        // Safari can't hold a vector map's WebGL context — src/utils/mapRendering.js.
        ...rasterOnSafari()
    }), [gestureHandling]);

    // Fit map bounds to show all leads
    const fitMapToLeads = useCallback((mapInstance) => {
        if (!mapInstance || leads.length === 0) return;

        if (leads.length === 1) {
            // Single lead - just center on it with reasonable zoom
            mapInstance.setCenter({ lat: leads[0].latitude, lng: leads[0].longitude });
            mapInstance.setZoom(14);
            return;
        }

        // Create bounds that encompass all leads
        const bounds = new window.google.maps.LatLngBounds();
        leads.forEach(lead => {
            bounds.extend({ lat: lead.latitude, lng: lead.longitude });
        });

        // Fit map to bounds with padding
        mapInstance.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
    }, [leads]);

    const onLoad = useCallback((mapInstance) => {
        setRawMap(mapInstance);
        // In map mode `initialBounds` drives the initial framing; fitting
        // to whatever `leads` happens to hold at mount (often stale results
        // from the table query) would cause a visible snap when the
        // initialBounds effect runs immediately after.
        if (viewportFilterActive) return;
        fitMapToLeads(mapInstance);
    }, [setRawMap, fitMapToLeads, viewportFilterActive]);

    // Clear the shared map instance when this map unmounts (e.g. toggling to
    // table view). `rawMap` lives in the parent, so without this it would
    // retain a dead instance; on the next map open the framing effect would
    // run against that stale map, mark its bounds as "applied", and then skip
    // framing the real new map — parking it at the default US-center view.
    const onUnmount = useCallback(() => {
        setRawMap(null);
    }, [setRawMap]);

    // Track previous leads to detect when data actually changes
    const prevLeadsRef = useRef(leads);
    // Initial-fit guard for the leads-based fit effect below. Once it has
    // run, this stays true so subsequent refetches don't create a fit →
    // idle → fetch → new leads → fit loop.
    const hasFitOnceRef = useRef(false);

    // Frame the map to the page-1 leads' bounding box (`initialBounds`) as
    // soon as the map and the box are both available. This runs regardless of
    // `viewportFilterActive` — it IS the on-open framing. We dedupe against the
    // last-applied box so it re-frames on a filter/sort change (which produces
    // a new box) but not on pan: `initialBounds` is derived from the table
    // query, which ignores map bounds, so panning never changes it and the
    // pan → idle → bounds-fetch cycle can't feed back into a re-fit loop.
    // Dedup is keyed to the map INSTANCE as well as the bounds values: a new
    // `rawMap` (remount / fresh GoogleMap) must always re-frame, even if the
    // bounds happen to be identical to what a prior, now-dead instance was
    // framed to. Keying on values alone let a stale instance "consume" the
    // framing and leave the real map at its default center.
    const lastAppliedBoundsRef = useRef(null);
    useEffect(() => {
        if (!rawMap) return;
        if (!initialBounds || typeof initialBounds.north !== 'number') return;
        const { north, south, east, west } = initialBounds;
        const last = lastAppliedBoundsRef.current;
        if (last && last.map === rawMap && last.north === north && last.south === south && last.east === east && last.west === west) return;
        // Degenerate box (single point or all-coincident leads): fitBounds
        // would zoom in maximally, so center + a sensible city zoom instead.
        if (north === south && east === west) {
            rawMap.setCenter({ lat: north, lng: east });
            rawMap.setZoom(13);
        } else {
            const bounds = new window.google.maps.LatLngBounds(
                { lat: south, lng: west },
                { lat: north, lng: east }
            );
            rawMap.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
        }
        lastAppliedBoundsRef.current = { map: rawMap, north, south, east, west };
        hasFitOnceRef.current = true;
    }, [rawMap, initialBounds]);

    // Re-fit bounds when leads data changes (pagination, filtering, search).
    // Skip when viewport filtering is active to prevent the feedback loop
    // (fit → idle → fetch → new leads → fit → ...). In map view this means
    // we don't re-fit after the first time, which is exactly what we want —
    // the initial fit above handles the on-open case.
    useEffect(() => {
        if (!rawMap || leads.length === 0) return;
        if (viewportFilterActive) return;

        // Check if leads have actually changed (compare by leadId)
        const prevIds = prevLeadsRef.current.map(l => l.leadId).sort().join(',');
        const currentIds = leads.map(l => l.leadId).sort().join(',');

        if (prevIds !== currentIds) {
            fitMapToLeads(rawMap);
            prevLeadsRef.current = leads;
            hasFitOnceRef.current = true;
        }
    }, [rawMap, leads, fitMapToLeads, viewportFilterActive]);

    // Report viewport bounds when map settles (after pan/zoom)
    const handleIdle = useCallback(() => {
        if (!rawMap || !onViewportChange) return;
        const bounds = rawMap.getBounds();
        if (!bounds) return;
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        onViewportChange({
            north: ne.lat(),
            south: sw.lat(),
            east: ne.lng(),
            west: sw.lng()
        });
    }, [rawMap, onViewportChange]);

    const toggleSatellite = useCallback(() => {
        if (rawMap) {
            const newMapType = mapType === "roadmap" ? "satellite" : "roadmap";
            rawMap.setMapTypeId(newMapType);
            setMapType(newMapType);
        }
    }, [rawMap, mapType, setMapType]);

    const handleMarkerClick = useCallback((lead) => {
        if (onLeadClick) {
            onLeadClick(lead);
        }
    }, [onLeadClick]);

    const handleMarkerHover = useCallback((leadId) => {
        if (onLeadHover) {
            onLeadHover(leadId);
        }
    }, [onLeadHover]);

    if (!isMapLoaded) {
        return null;
    }

    return (
        <GoogleMap
            zoom={10}
            center={mapCenter}
            mapContainerClassName="leads-map-container"
            options={options}
            onLoad={onLoad}
            onUnmount={onUnmount}
            onIdle={handleIdle}
        >
            {rawMap && innerWidth >= 1000 && (
                <MapModeToggle
                    toggleSatellite={toggleSatellite}
                    mapType={mapType}
                />
            )}
            {rawMap && leads.map((lead) => {
                const isClicked = clickedLeadId === lead.leadId;
                const isHovered = hoverLeadId === lead.leadId;
                const isNew = newLeadIds ? newLeadIds.has(lead.leadId) : false;
                // Clicked = highest z-index, hovered = medium, default = low.
                // A just-arrived marker floats above the pack so its ripple
                // reads clearly over neighbouring pins.
                const zIndex = isClicked ? 1000 : (isNew ? 500 : (isHovered ? 100 : 1));

                return (
                    <Marker
                        key={lead.leadId}
                        map={rawMap}
                        position={{ lat: lead.latitude, lng: lead.longitude }}
                        onClick={() => handleMarkerClick(lead)}
                        zIndex={zIndex}
                    >
                        <div
                            onMouseEnter={() => handleMarkerHover(lead.leadId)}
                            onMouseLeave={() => handleMarkerHover(null)}
                        >
                            <LeadMapBubble
                                price={lead.currentAskingPrice}
                                isHovered={isHovered}
                                isClicked={isClicked}
                                isNew={isNew}
                                verdict={getMarkerVerdict ? getMarkerVerdict(lead) : undefined}
                            />
                        </div>
                    </Marker>
                );
            })}
        </GoogleMap>
    );
}

export default React.memo(LeadsMap);
